<template>
  <div class="SearchExperienceCategoryFilters-base">
    <ai-separator class="separator" margin="none" />

    <div
      ref="container"
      class="SearchExperienceCategoryFilters-experienceCategories">
      <div
        v-if="showLeftButton"
        class="SearchExperienceCategoryFilters-experienceCategoriesButtonContainer SearchExperienceCategoryFilters-experienceCategoriesButtonContainer--Left">
        <button
          tabindex="-1"
          class="SearchExperienceCategoryFilters-experienceCategoriesButton SearchExperienceCategoryFilters-experienceCategoriesButton--Left"
          @click="goLeft">
          <ai-icon
            name="chevron-left"
            class="SearchExperienceCategoryFilters-experienceCategoriesButtonIcon"
            :size="16"
            :description="$t('accessibility.description.leftButton')" />
        </button>
      </div>

      <div
        ref="slider"
        role="list"
        class="SearchExperienceCategoryFilters-experienceCategoriesContainer"
        :style="{ transform: `translateX(${translateAmount}px)` }"
        :aria-label="$t('accessibility.description.experienceCategories')">
        <search-experience-category-carousel-item
          :id="defaultButtonId"
          ref="defaultButton"
          icon-name="world-ball"
          :label="$t('search.filter.value.allExperienceCategories')" />

        <search-experience-category-carousel-item
          v-for="category in displayedCategories"
          :id="category.id ?? ''"
          ref="collection"
          :key="category.id ?? ''"
          :src="category.logo?.src as string"
          :alt="category.logo?.alt as string"
          :label="category.title as string"
          @click="sendApplyFilterEvent(category.title as string)" />
      </div>

      <div
        v-if="showRightButton"
        class="SearchExperienceCategoryFilters-experienceCategoriesButtonContainer SearchExperienceCategoryFilters-experienceCategoriesButtonContainer--Right">
        <button
          tabindex="-1"
          class="SearchExperienceCategoryFilters-experienceCategoriesButton"
          @click="goRight">
          <ai-icon
            class="SearchExperienceCategoryFilters-experienceCategoriesButtonIcon"
            name="chevron-right"
            :size="16"
            :description="$t('accessibility.description.rightButton')" />
        </button>
      </div>

      <client-only>
        <div class="SearchExperienceCategoryFilters-progressBarContainer">
          <ai-progress-bar
            class="SearchExperienceCategoryFilters-progressBar"
            :current-step="currentStep"
            :steps="numberOfStepsInSlider" />
        </div>
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import throttle from 'lodash/throttle';

import SearchExperienceCategoryCarouselItem from './carouselItem/SearchExperienceCategoryCarouselItem.vue';

import type { ExperienceCategory } from '~~/domains/graphql';
import { useExperienceCategoriesAsyncData } from '~~/domains/graphql';
import { useSearchStore } from '~~/domains/search/store';
import { event, eventCustom } from '~~/helpers/gtm.helper';

const searchStore = useSearchStore();

const { data } = await useExperienceCategoriesAsyncData();

const experienceCategories = computed(
  () => (data.value?.experienceCategories ?? []) as ExperienceCategory[],
);

searchStore.setExperienceCategories(experienceCategories.value);

const displayedCategories = computed(
  () => searchStore.availableExperienceCategories,
);

const isLargeDesktop = useMatchMediaQuery('large-desktop');

const container = ref<HTMLElement>();
const slider = ref<HTMLElement>();
const defaultButton = ref<Record<string, HTMLElement>>();
const defaultButtonId = 'all';
const collection = ref<Record<string, HTMLElement>[]>();

const sliderWidth = ref(0);
const containerWidth = ref(0);
const translateAmount = ref(0);
const stepWidth = ref(0);
const numberOfStepsInSlider = computed(
  () =>
    Math.ceil((sliderWidth.value - containerWidth.value) / stepWidth.value) + 1,
);
const currentStep = computed(
  () => Math.abs(translateAmount.value / stepWidth.value) + 1,
);
const showLeftButton = computed(() => currentStep.value > 1);
const showRightButton = computed(
  () => currentStep.value < numberOfStepsInSlider.value,
);

const goLeft = () => {
  if (currentStep.value <= 1) {
    return;
  }

  if (currentStep.value > numberOfStepsInSlider.value) {
    translateAmount.value = -(
      numberOfStepsInSlider.value * stepWidth.value -
      stepWidth.value
    );
  } else if (currentStep.value % 1 === 0) {
    translateAmount.value += stepWidth.value;
  } else {
    translateAmount.value =
      -(Math.floor(currentStep.value) * stepWidth.value) + stepWidth.value;
  }
};

const goRight = () => {
  if (currentStep.value >= numberOfStepsInSlider.value) {
    return;
  }

  if (currentStep.value < 0) {
    translateAmount.value = 0;
  } else if (currentStep.value % 1 === 0) {
    translateAmount.value -= stepWidth.value;
  } else {
    translateAmount.value = -(
      Math.ceil(currentStep.value) * stepWidth.value -
      stepWidth.value
    );
  }
};

const resetSlider = () => {
  translateAmount.value = 0;
};

const scrollToCategory = (experienceCategoryId?: string | null) => {
  if (!experienceCategoryId) return;

  nextTick(() => {
    const indexInCarousel = experienceCategories.value.findIndex(
      category => category.id === experienceCategoryId,
    );

    if (indexInCarousel >= 0) {
      for (let i = 0; i < indexInCarousel; i++) {
        goRight();
      }
    }
  });
};

const updateBoundaries = throttle(() => {
  resetSlider();

  sliderWidth.value = slider.value?.getBoundingClientRect().width ?? 0;
  containerWidth.value = container.value?.getBoundingClientRect().width ?? 0;

  if (!defaultButton.value || !defaultButton.value.item) {
    return;
  }

  const defaultSize = Math.floor(
    defaultButton.value.item.getBoundingClientRect().width,
  );
  const marginSize = isLargeDesktop ? 24 : 16;
  stepWidth.value = defaultSize + marginSize;
}, 200);

const route = useRoute();

const sendApplyFilterEvent = (filterLabel: string) => {
  event('apply_filter', {
    event_name: 'apply_filter',
    category: 'booking funnel - hotel list',
    action: 'filter – click on cta - add',
    label: filterLabel,
    filter_name: filterLabel,
  });

  eventCustom('Generic Event', {
    event_name: 'filter',
    filter: {
      filter_experience: filterLabel,
      destination: searchStore.destination.text,
    },
  });
};

onMounted(() => {
  if (process.client) {
    updateBoundaries();

    window.addEventListener('scroll', updateBoundaries);
    window.addEventListener('resize', updateBoundaries);
  }

  const experienceCategoryId = route.query.experienceCategoryId as string;
  if (experienceCategoryId) {
    searchStore.experienceCategoryId = experienceCategoryId;
    scrollToCategory(experienceCategoryId);
  } else {
    searchStore.experienceCategoryId = null;
  }
});

onBeforeUnmount(() => {
  if (process.client) {
    window.removeEventListener('scroll', updateBoundaries);
    window.removeEventListener('resize', updateBoundaries);
  }
});

useMobileTouchScroll(slider, translateAmount, { snap: stepWidth });

watch(
  () => displayedCategories.value,
  () => {
    resetSlider();

    nextTick(() => {
      updateBoundaries();
      scrollToCategory(searchStore.experienceCategoryId);
    });
  },
);

watch(
  () => searchStore.searchHotelsResult.hotels,
  () => {
    if (
      !displayedCategories.value.some(
        category => category.id === searchStore.experienceCategoryId,
      )
    ) {
      searchStore.experienceCategoryId = defaultButtonId;
    }
  },
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/functions' as func;

.SearchExperienceCategoryFilters-base {
  width: 100%;

  @media (mq.$from-large) {
    background: colors.$gold-100;
  }
}

.SearchExperienceCategoryFilters-experienceCategories {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
}

.SearchExperienceCategoryFilters-experienceCategoriesContainer {
  display: flex;
  padding-bottom: constants.$padding-01;
  flex-direction: row;
  align-items: center;
  vertical-align: baseline;
  margin: auto;
  transition: transform 500ms;
  gap: constants.$margin-01;

  @media (mq.$from-large) {
    gap: constants.$margin-02;
    padding: constants.$padding-02 constants.$padding-03 constants.$padding-01;
  }
}

.SearchExperienceCategoryFilters-experienceCategoriesButtonContainer {
  position: absolute;
  top: 0;
  height: 100%;
  width: func.calcRem(calc(32 + 16)); // 32 base button + margins
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  pointer-events: none;

  &--Left {
    left: 0;

    background: linear-gradient(
      90.12deg,
      colors.$white 21.36%,
      rgba(255, 255, 255, 0) 94.47%
    );

    @media (mq.$from-large) {
      width: func.calcRem(96);
      background: linear-gradient(
        90.12deg,
        colors.$gold-100 21.36%,
        rgba(245, 242, 233, 0) 94.47%
      );
    }
  }

  &--Right {
    right: 0;

    background: linear-gradient(
      270.12deg,
      colors.$white 21.36%,
      rgba(255, 255, 255, 0) 94.47%
    );

    @media (mq.$from-large) {
      width: func.calcRem(96);
      background: linear-gradient(
        270.12deg,
        colors.$gold-100 21.36%,
        rgba(245, 242, 233, 0) 94.47%
      );
    }
  }
}

.SearchExperienceCategoryFilters-experienceCategoriesButton {
  width: func.calcRem(32);
  height: func.calcRem(32);
  border-radius: 100%;
  background-color: colors.$gold-900;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: all;
}

.SearchExperienceCategoryFilters-experienceCategoriesButtonIcon {
  color: white;
}

.separator {
  display: none;

  @media (mq.$from-large) {
    display: block;
    margin: 0 constants.$margin-02;
  }
}

.stroke {
  display: none;

  @media (mq.$from-large) {
    display: block;
    height: 100%;
    background-color: colors.$gold-200;
    margin-left: constants.$margin-02;
    margin-right: constants.$margin-02;
  }
}

.SearchExperienceCategoryFilters-progressBarContainer {
  width: 100%;
  height: 1px;

  display: flex;
  padding-left: constants.$padding-02;
  padding-right: constants.$padding-02;

  @media (mq.$from-large) {
    display: none;
  }
}
</style>
